export interface FloorPlanLabel {
  id: string;
  x: number;
  y: number;
  name: string;
  area: string;
  /** Rotation in degrees around the normalized label position. */
  rotation: number;
}

export type FloorPlanLabelWeight = 400 | 500;

export interface FloorPlanLabelStyle {
  /** Relative to the shorter image edge; keeps preview and export proportional. */
  nameSizeRatio: number;
  /** The area line is intentionally lighter and smaller than the room name. */
  areaScale: number;
  nameColour: string;
  areaColour: string;
  nameWeight: FloorPlanLabelWeight;
  lineHeight: number;
}

export interface FloorPlanLabelDocument {
  version: 1;
  labels: FloorPlanLabel[];
  style: FloorPlanLabelStyle;
}

export const DEFAULT_FLOORPLAN_LABEL_STYLE: FloorPlanLabelStyle = Object.freeze({
  nameSizeRatio: 0.0175,
  areaScale: 0.78,
  nameColour: '#171717',
  areaColour: '#666666',
  nameWeight: 400,
  lineHeight: 1.15,
});

export const FLOORPLAN_LABEL_ROTATION_LIMIT = 180;

export function normalizeFloorPlanArea(value: string): string {
  return value.trim().replace(/m\s*(?:\^\s*2|2|²)/gi, 'm²');
}

export function normalizeFloorPlanRotation(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return Math.max(-FLOORPLAN_LABEL_ROTATION_LIMIT, Math.min(FLOORPLAN_LABEL_ROTATION_LIMIT, Math.round(value)));
}

export function getFloorPlanLabelFontSizes(width: number, height: number, style: FloorPlanLabelStyle): { name: number; area: number } {
  const shortEdge = Math.min(width, height);
  const name = Math.max(12, Math.round(shortEdge * style.nameSizeRatio));
  return { name, area: Math.max(10, Math.round(name * style.areaScale)) };
}

function isUnitInterval(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 && value <= 1;
}

function parseLabelStyle(value: unknown): FloorPlanLabelStyle {
  const source = value && typeof value === 'object' ? value as Partial<FloorPlanLabelStyle> : {};
  const style: FloorPlanLabelStyle = {
    nameSizeRatio: typeof source.nameSizeRatio === 'number' && source.nameSizeRatio > 0 && source.nameSizeRatio <= 0.1 ? source.nameSizeRatio : DEFAULT_FLOORPLAN_LABEL_STYLE.nameSizeRatio,
    areaScale: typeof source.areaScale === 'number' && source.areaScale > 0 && source.areaScale <= 1 ? source.areaScale : DEFAULT_FLOORPLAN_LABEL_STYLE.areaScale,
    nameColour: typeof source.nameColour === 'string' && source.nameColour ? source.nameColour : DEFAULT_FLOORPLAN_LABEL_STYLE.nameColour,
    areaColour: typeof source.areaColour === 'string' && source.areaColour ? source.areaColour : DEFAULT_FLOORPLAN_LABEL_STYLE.areaColour,
    nameWeight: source.nameWeight === 500 ? 500 : source.nameWeight === 400 ? 400 : DEFAULT_FLOORPLAN_LABEL_STYLE.nameWeight,
    lineHeight: typeof source.lineHeight === 'number' && source.lineHeight >= 1 && source.lineHeight <= 2 ? source.lineHeight : DEFAULT_FLOORPLAN_LABEL_STYLE.lineHeight,
  };
  return style;
}

export function normalizeFloorPlanLabelDocument(value: unknown): FloorPlanLabelDocument {
  if (!value || typeof value !== 'object') throw new Error('Die Beschriftungsdatei ist ungültig.');
  const source = value as Partial<FloorPlanLabelDocument>;
  if (source.version !== 1 || !Array.isArray(source.labels)) throw new Error('Die Beschriftungsdatei hat ein unbekanntes Format.');
  const labels = source.labels.map((entry, index) => {
    if (!entry || typeof entry !== 'object') throw new Error(`Beschriftung ${index + 1} ist ungültig.`);
    const label = entry as Partial<FloorPlanLabel>;
    if (!isUnitInterval(label.x) || !isUnitInterval(label.y) || typeof label.name !== 'string' || !label.name.trim()) {
      throw new Error(`Beschriftung ${index + 1} enthält keine gültige Position oder keinen Raumnamen.`);
    }
    return {
      id: typeof label.id === 'string' && label.id ? label.id : `label-${index + 1}`,
      x: label.x,
      y: label.y,
      name: label.name,
      area: typeof label.area === 'string' ? normalizeFloorPlanArea(label.area) : '',
      rotation: normalizeFloorPlanRotation(label.rotation),
    };
  });
  return { version: 1, labels, style: parseLabelStyle(source.style) };
}

export function createFloorPlanLabelDocument(labels: FloorPlanLabel[], style: FloorPlanLabelStyle): FloorPlanLabelDocument {
  return normalizeFloorPlanLabelDocument({ version: 1, labels, style });
}
